import IconInfo from '@apollo/icons/default/IconInfo.svg';
import {
  Button,
  ButtonGroup,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Select,
} from '@apollo/orbit';
import classnames from 'classnames';
import { useFormik } from 'formik';
import React, { useCallback } from 'react';
import * as Yup from 'yup';

import { ClickableText } from 'src/components/common/clickableText/ClickableText';
import { Form as CustomForm } from 'src/components/common/form/Form';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { DatadogApiRegion } from 'src/lib/graphqlTypes/types';
import { DocsRouter } from 'src/lib/routers';

import {
  useSetDatadogApiKeyAndRegion,
  useSetDatadogEnabled,
} from '../useUpdateDatadogConfig';

interface Props {
  isOpen: boolean;
  serviceId: string;
  apiKey?: string;
  apiRegion?: DatadogApiRegion;
  currentlyEnabled?: boolean;
  onClose: () => void;
}

// Maps older enum values to their newer counterparts.
// We can remove this once we migrate US => US1 and EU => EU1 in the DB.
const apiRegionMigrator = new Map<DatadogApiRegion, DatadogApiRegion>([
  [DatadogApiRegion.US, DatadogApiRegion.US1],
  [DatadogApiRegion.US1, DatadogApiRegion.US1],
  [DatadogApiRegion.US3, DatadogApiRegion.US3],
  [DatadogApiRegion.US5, DatadogApiRegion.US5],
  [DatadogApiRegion.EU, DatadogApiRegion.EU1],
  [DatadogApiRegion.EU1, DatadogApiRegion.EU1],
  [DatadogApiRegion.US1FED, DatadogApiRegion.US1FED],
]);

interface ApiRegionOption {
  apiRegion: DatadogApiRegion;
  displayName: string;
  site: string;
}

const apiRegionOptions = new Map<DatadogApiRegion, ApiRegionOption>([
  [
    DatadogApiRegion.US1,
    {
      apiRegion: DatadogApiRegion.US1,
      displayName: 'US1',
      site: 'app.datadoghq.com',
    },
  ],
  [
    DatadogApiRegion.US3,
    {
      apiRegion: DatadogApiRegion.US3,
      displayName: 'US3',
      site: 'us3.datadoghq.com',
    },
  ],
  [
    DatadogApiRegion.US5,
    {
      apiRegion: DatadogApiRegion.US5,
      displayName: 'US5',
      site: 'us5.datadoghq.com',
    },
  ],
  [
    DatadogApiRegion.EU1,
    {
      apiRegion: DatadogApiRegion.EU1,
      displayName: 'EU1',
      site: 'app.datadoghq.eu',
    },
  ],
  [
    DatadogApiRegion.US1FED,
    {
      apiRegion: DatadogApiRegion.US1FED,
      displayName: 'US1-FED',
      site: 'app.ddog-gov.com',
    },
  ],
]);

/**
 * Schema for the form
 */
const validationSchema = Yup.object().shape({
  apiKey: Yup.string()
    .trim()
    .required('API Key is required')
    .max(64, 'Must be at most 64 characters'),
  apiRegion: Yup.mixed<DatadogApiRegion | null>()
    .required('API Region is required')
    .oneOf(Object.values(DatadogApiRegion), 'API Region is required'),
});

/**
 * Types inferred from `validationSchema`
 */
type FormValues = Yup.InferType<typeof validationSchema>;

export const DatadogConfigModal = ({
  isOpen,
  serviceId,
  apiKey,
  apiRegion,
  currentlyEnabled,
  onClose,
}: Props) => {
  const { setDatadogApiKeyAndRegion, error: setApiKeyAndRegionError } =
    useSetDatadogApiKeyAndRegion();
  const {
    setDatadogEnabled,
    loading: enableLoading,
    error: enableError,
  } = useSetDatadogEnabled();
  const {
    errors,
    values,
    touched,
    handleChange,
    handleSubmit,
    isSubmitting: formikIsSubmitting,
  } = useFormik<FormValues>({
    initialValues: {
      apiKey: apiKey || '',
      apiRegion: apiRegion || null,
    },
    async onSubmit(formValues) {
      try {
        await setDatadogApiKeyAndRegion({
          serviceId,
          apiKey: formValues.apiKey,
          apiRegion: formValues.apiRegion,
        });
        onClose();
      } catch {
        // We don't need to do anything to handle this here since the error will
        // come back in the `error` from our useMutation hook
      }
    },
    validationSchema,
  });

  const handleEnabledToggle = useCallback(
    () =>
      setDatadogEnabled({
        serviceId,
        enabled: !currentlyEnabled,
      }),
    [serviceId, setDatadogEnabled, currentlyEnabled],
  );

  /**
   * Use this for all loading states
   */
  const isSubmitting = formikIsSubmitting;
  const submitActionText = currentlyEnabled ? 'Update Configuration' : 'Enable';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <CustomForm onSubmit={handleSubmit} noValidate={true}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Update Datadog Configuration</ModalTitle>
            <ModalDescription>
              {apiKey ? (
                <>
                  Datadog integration for this graph is currently{' '}
                  <span
                    className={classnames(
                      'font-semibold',
                      currentlyEnabled ? 'text-link' : 'text-red',
                    )}
                  >
                    {currentlyEnabled ? 'enabled' : 'disabled'}.
                  </span>
                </>
              ) : (
                'Provide a Datadog API Key and Region to set up Datadog integration.'
              )}
            </ModalDescription>
          </ModalHeader>
          <ModalBody isScrollable={false}>
            <FormControl
              id="apiKey"
              data-testid="apiKey control"
              isRequired
              isInvalid={!!errors.apiKey && !!touched.apiKey}
            >
              <FormLabel data-testid="apiKey label">
                API Key{' '}
                <Tooltip
                  interactive
                  label={
                    <>
                      <Link
                        to={DocsRouter.path('DatadogIntegration')}
                        isExternal
                      >
                        See the docs
                      </Link>{' '}
                      to learn how to generate a Datadog API Key.
                    </>
                  }
                >
                  <IconInfo className="ml-2 text-secondary" tabIndex={0} />
                </Tooltip>
              </FormLabel>
              <Input
                autoFocus
                isDisabled={isSubmitting}
                name="apiKey"
                onChange={handleChange}
                placeholder="Your Datadog API Key"
                spellCheck={false}
                value={values.apiKey}
              />
              <FormErrorMessage>{errors.apiKey}</FormErrorMessage>
            </FormControl>
            <FormControl
              className="mt-2"
              id="apiRegion"
              data-testid="apiRegion control"
              isRequired
              isInvalid={!!errors.apiRegion}
            >
              <FormLabel
                className="flex items-center"
                data-testid="apiRegion label"
              >
                API Region{' '}
                <Tooltip
                  interactive
                  label={
                    <>
                      To determine your Datadog API Region, go to your Datadog
                      account, and look at the site's domain name.{' '}
                      <ClickableText
                        as="a"
                        className="underline"
                        target="_blank"
                        rel="noopener noreferrer"
                        href="https://docs.datadoghq.com/getting_started/site/"
                      >
                        Visit the Datadog docs to learn more about sites.
                      </ClickableText>
                    </>
                  }
                >
                  <IconInfo className="ml-2 text-secondary" tabIndex={0} />
                </Tooltip>
              </FormLabel>
              <Select
                name="apiRegion"
                onChange={handleChange}
                value={
                  values.apiRegion
                    ? apiRegionMigrator.get(values.apiRegion)
                    : ''
                }
                placeholder="Choose an API Region"
              >
                {Array.from(apiRegionOptions.values()).map(
                  (apiRegionOption) => (
                    <option
                      key={apiRegionOption.apiRegion}
                      value={apiRegionOption.apiRegion}
                    >
                      {apiRegionOption.displayName} {apiRegionOption.site}
                    </option>
                  ),
                )}
              </Select>
              <FormErrorMessage>{errors.apiRegion}</FormErrorMessage>
            </FormControl>
            {(setApiKeyAndRegionError || enableError) && (
              <ErrorMessage className="mb-10" />
            )}
          </ModalBody>
          <ModalFooter>
            {apiKey && currentlyEnabled && (
              <Button
                variant="secondary"
                onClick={onClose}
                type="button"
                isDisabled={isSubmitting}
              >
                Cancel
              </Button>
            )}
            <ButtonGroup>
              {apiKey && currentlyEnabled ? (
                <Button
                  variant={currentlyEnabled ? 'destructive' : 'primary'}
                  isDisabled={!apiKey}
                  isLoading={enableLoading}
                  loadingText={currentlyEnabled ? 'Disable' : 'Enable'}
                  type="button"
                  onClick={handleEnabledToggle}
                >
                  {currentlyEnabled ? 'Disable' : 'Enable'}
                </Button>
              ) : (
                <Button
                  variant="secondary"
                  onClick={onClose}
                  type="button"
                  isDisabled={isSubmitting}
                >
                  Cancel
                </Button>
              )}
              <Button
                variant="primary"
                isLoading={isSubmitting}
                loadingText={submitActionText}
                type="submit"
              >
                {submitActionText}
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </CustomForm>
    </Modal>
  );
};
